import { OPERATIONAL_EVENT_TYPE, UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useMetricsContext } from '../context/metrics';
import { FORGE_UI_ANALYTICS_CHANNEL } from '.';
import { useMemoOne } from 'use-memo-one';
import { fg } from '@atlaskit/platform-feature-flags';
import type { FeatureFlagEnv, FeatureFlagEventTypes } from '../custom-ui/bridge/types';
import { extensionLoadedEventFilter } from './extensionLoadedFilter';
import { isUnrecoverableError } from '../custom-ui/bridge/utils/isUnrecoverableError';
import type { PermissionMethod } from '../custom-ui/bridge/methods/permissions/usePermission';
type EnvironmentType = 'PRODUCTION' | 'DEVELOPMENT' | 'STAGING';

type Components = Record<
	string,
	{
		count: number;
		propCount: Record<string, number>;
	}
>;

type BaseEventPayload = {
	source: string;
};

type TrackComponentUsageRenderedPayload = BaseEventPayload & {
	components: Components;
	forgeEnvironment: EnvironmentType;
	forgeReactMajorVersion: string | null;
};

type TrackRendererRenderedPayload = BaseEventPayload & {
	target: 'renderer';
};

type TrackBridgePayload = BaseEventPayload & {
	additionalAttributes?: Record<string, unknown>;
	forgeEnvironment?: EnvironmentType;
	method: string;
};

// For extensibility
type TrackBridgeSucceedPayload = TrackBridgePayload;

type TrackBridgeFailedPayload = TrackBridgePayload & {
	errorName: string;
};

type TrackExtensionLoadedPayload = BaseEventPayload & {
	extensionType: 'extension' | 'inlineExtension' | 'bodiedExtension';
	forgeEnvironment?: EnvironmentType;
	// Used for tracking custom macro config loading
	isConfiguring?: boolean;
	isInserting?: boolean;
	renderType: 'CustomUI' | 'UIKit';
	// Used for tracking modal extension points viewport size
	viewportSize?: string;
};

type TrackExtensionRemountedPayload = BaseEventPayload & {
	isUseBridgeReinit?: boolean;
	mountCount: number;
};

type TrackForgeFeatureFlagEventPayload = BaseEventPayload & {
	appId: string;
	appType: 'CustomUI' | 'UIKit';
	baseUrl: string;
	eventType: FeatureFlagEventTypes;
	featureFlagEnv: FeatureFlagEnv;
	flagName?: string;
	forgeEnvironment?: EnvironmentType;
	success: boolean;
};

type TrackObjectStoreActionPayload = BaseEventPayload & {
	action: 'upload' | 'download' | 'getMetadata' | 'delete';
	appId: string;
	appType: 'CustomUI' | 'UIKit';
	forgeEnvironment?: EnvironmentType;
};

type TrackPermissionActionPayload = BaseEventPayload & {
	action: PermissionMethod;
	appId: string;
	installationId: string;
};

type TrackHandShakeMetricPayload = BaseEventPayload & {
	error?: Error;
	success: boolean;
};

type TrackEmbeddedMacroRejectedPayload = BaseEventPayload & {
	extensionId: string;
	reason: 'not_installed';
};

type FeatureFlagEventActionType =
	| FeatureFlagEventTypes.INITIALIZATION
	| FeatureFlagEventTypes.CHECKFLAG;

type ObjectStoreActionType = 'upload' | 'download' | 'getMetadata' | 'delete';

type FireEventOptions = { filter?: (event: UIAnalyticsEvent) => boolean; sampleRate?: number };

type FireEventPayload = {
	action:
	| 'succeeded'
	| 'failed'
	| 'loaded'
	| 'rendered'
	| 'remounted'
	| 'rejected'
	| 'performance.profiled'
	| 'contextTokenComparison'
	| FeatureFlagEventActionType
	| ObjectStoreActionType
	| PermissionMethod;
	actionSubject:
	| 'forge.ui.bridge'
	| 'forge.ui.analytics.component_usage'
	| 'forge.ui.extension'
	| 'forge.ui.renderer'
	| 'forge.ui.invocation'
	| 'forge.ui.feature_flag'
	| 'forge.ui.object_store'
	| 'forge.ui.embedded_macro'
	| 'forge.ui.permission';

	attributes?: Record<string, unknown>;
	source: string;
};

export const fireEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ action, actionSubject, source, attributes }: FireEventPayload,
	options?: FireEventOptions,
): void => {
	const { sampleRate, filter } = options ?? {};
	// If a sample rate is provided, we can use it to determine whether to fire the event
	if (sampleRate) {
		const randomValue = Math.random();
		if (randomValue > sampleRate) {
			return; // Don't fire the event
		}
	}

	const event = createAnalyticsEvent({
		eventType: OPERATIONAL_EVENT_TYPE,
		data: {
			action,
			actionSubject,
			attributes,
			source,
			tags: ['forge'],
		},
	});

	// Don't fire the event if there's a filter and event does not pass it
	if (filter && !filter(event)) {
		return;
	}

	event.fire(FORGE_UI_ANALYTICS_CHANNEL);
};

export type FireUIEventPayload = {
	action: string;
	actionSubject: string;
	attributes?: Record<string, unknown>;
};

export const fireUIEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ action, actionSubject, attributes }: FireUIEventPayload,
): void => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		data: { action, actionSubject, attributes },
	}).fire(FORGE_UI_ANALYTICS_CHANNEL);
};

// Ideally this should be removed entirely in the future.
// see: https://hello.atlassian.net/wiki/spaces/ECO/pages/5687458412/Why+is+GASv3+Event+Costs+and+Volumes+Climbing+Cost+Saving+Proposal
const legacyTrackBridgeSucceed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, method, additionalAttributes, forgeEnvironment }: TrackBridgeSucceedPayload,
) =>
	fireEvent(createAnalyticsEvent, {
		action: 'succeeded',
		actionSubject: 'forge.ui.bridge',
		source,
		attributes: {
			...(additionalAttributes ?? {}),
			method,
			forgeEnvironment,
		},
	});

// We limit the type of bridge methods we track to only the ones we are analysing in databricks to avoid sending too many events
const allowedBridgeMethods: Record<string, { allowed: boolean; options?: FireEventOptions }> = {
	navigate: {
		allowed: true,
		options: {
			sampleRate: 0.1,
		},
	},
	openRovo: {
		allowed: true,
	},
	isRovoEnabled: {
		allowed: true,
	},
	// PIR-30455: The following three methods (openModal, close, onClose) are temporarily tracked as we have a high frequency of changes to the modal extension points
	// TODO cleanup: https://ecosystem-platform.atlassian.net/browse/EXT-4533
	openModal: {
		allowed: true,
	},
	close: {
		allowed: true,
	},
	onClose: {
		allowed: true,
	},
};

const optimisedTrackBridgeSucceed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, method, additionalAttributes, forgeEnvironment }: TrackBridgeSucceedPayload,
) => {
	if (allowedBridgeMethods[method]?.allowed) {
		fireEvent(
			createAnalyticsEvent,
			{
				action: 'succeeded',
				actionSubject: 'forge.ui.bridge',
				source,
				attributes: {
					...(additionalAttributes ?? {}),
					method,
					forgeEnvironment,
				},
			},
			allowedBridgeMethods[method]?.options,
		);
	}
};

const trackForgeFeatureFlagEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		appType,
		baseUrl,
		appId,
		flagName,
		eventType,
		success,
		featureFlagEnv,
		forgeEnvironment,
	}: TrackForgeFeatureFlagEventPayload,
) => {
	fireEvent(createAnalyticsEvent, {
		action: eventType,
		actionSubject: 'forge.ui.feature_flag',
		source,
		attributes: {
			appType,
			baseUrl,
			appId,
			flagName,
			eventType,
			success,
			featureFlagEnv,
			forgeEnvironment,
		},
	});
};

const trackObjectStoreAction = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, appType, appId, action, forgeEnvironment }: TrackObjectStoreActionPayload,
) => {
	fireEvent(createAnalyticsEvent, {
		action,
		actionSubject: 'forge.ui.object_store',
		source,
		attributes: {
			appType,
			appId,
			forgeEnvironment,
		},
	});
};

const trackPermissionAction = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, appId, installationId, action }: TrackPermissionActionPayload,
) => {
	fireEvent(createAnalyticsEvent, {
		action,
		actionSubject: 'forge.ui.permission',
		source,
		attributes: {
			appId,
			installationId,
		},
	});
};

export const trackBridgeSucceed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	payload: TrackBridgeSucceedPayload,
): void =>
	fg('forge-ui-event-cost-optimisation')
		? optimisedTrackBridgeSucceed(createAnalyticsEvent, payload)
		: legacyTrackBridgeSucceed(createAnalyticsEvent, payload);

export const trackBridgeFailed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, errorName, method, forgeEnvironment, additionalAttributes }: TrackBridgeFailedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'failed',
		actionSubject: 'forge.ui.bridge',
		source,
		attributes: {
			...(additionalAttributes ?? {}),
			error: errorName,
			method,
			forgeEnvironment,
		},
	});

export const trackComponentUsageRendered = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		forgeEnvironment,
		components,
		forgeReactMajorVersion,
	}: TrackComponentUsageRenderedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'rendered',
		actionSubject: 'forge.ui.analytics.component_usage',
		source,
		attributes: {
			forgeEnvironment,
			components,
			forgeReactMajorVersion,
		},
	});

export const trackRendererRendered = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, target }: TrackRendererRenderedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'rendered',
		actionSubject: 'forge.ui.renderer',
		source,
		attributes: {
			target,
		},
	});

const trackExtensionLoaded = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		source,
		forgeEnvironment,
		renderType,
		extensionType,
		isConfiguring,
		isInserting,
		viewportSize,
	}: TrackExtensionLoadedPayload,
) => {
	const options = fg('forge-ui-event-cost-optimisation')
		? {
			filter: extensionLoadedEventFilter,
		}
		: undefined;
	fireEvent(
		createAnalyticsEvent,
		{
			action: 'loaded',
			actionSubject: 'forge.ui.extension',
			source,
			attributes: {
				forgeEnvironment,
				renderType,
				extensionType,
				isConfiguring,
				isInserting,
				viewportSize,
			},
		},
		options,
	);
};

const trackExtensionRemounted = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, mountCount, isUseBridgeReinit }: TrackExtensionRemountedPayload,
) =>
	fireEvent(createAnalyticsEvent, {
		action: 'remounted',
		actionSubject: 'forge.ui.extension',
		source,
		attributes: {
			mountCount,
			isUseBridgeReinit,
		},
	});

const trackHandShakeMetric = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, success, error }: TrackHandShakeMetricPayload,
) => {
	if (error) {
		fireEvent(createAnalyticsEvent, {
			action: 'failed',
			actionSubject: 'forge.ui.bridge',
			source,
			attributes: {
				target: 'handshake',
				success,
				reason: error instanceof Error ? error.message : 'Unknown error',
				isUnrecoverable: isUnrecoverableError(error),
			},
		});
	} else {
		// Sample 10% of the events to reduce the costs
		// We only need 10% of the good events for the same SLO target
		fireEvent(
			createAnalyticsEvent,
			{
				action: 'succeeded',
				actionSubject: 'forge.ui.bridge',
				source,
				attributes: {
					target: 'handshake',
					success,
				},
			},
			{ sampleRate: 0.1 },
		);
	}
};

export const trackEmbeddedMacroRejected = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ source, extensionId, reason }: TrackEmbeddedMacroRejectedPayload,
): void =>
	fireEvent(createAnalyticsEvent, {
		action: 'rejected',
		actionSubject: 'forge.ui.embedded_macro',
		source,
		attributes: {
			extensionId,
			extensionType: 'xen:macro',
			reason,
		},
	});

type CreateForgeUiAnalyticsEventTrackersParams = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	source: string;
};

const createForgeUiAnalyticsEventTrackers = ({
	createAnalyticsEvent,
	source,
}: CreateForgeUiAnalyticsEventTrackersParams) => {
	return {
		trackHandShakeMetric: ({ source, success, error }: TrackHandShakeMetricPayload): void =>
			trackHandShakeMetric(createAnalyticsEvent, { source, success, error }),
		trackComponentUsageRendered: ({
			forgeEnvironment,
			components,
			forgeReactMajorVersion,
		}: Omit<TrackComponentUsageRenderedPayload, 'source'>): void =>
			trackComponentUsageRendered(createAnalyticsEvent, {
				source,
				forgeEnvironment,
				components,
				forgeReactMajorVersion,
			}),
		trackRendererRendered: ({ target }: Omit<TrackRendererRenderedPayload, 'source'>): void =>
			trackRendererRendered(createAnalyticsEvent, { source, target }),
		trackExtensionLoaded: ({
			forgeEnvironment,
			renderType,
			extensionType,
			isConfiguring,
			isInserting,
			viewportSize,
		}: Omit<TrackExtensionLoadedPayload, 'source'>): void =>
			trackExtensionLoaded(createAnalyticsEvent, {
				source,
				forgeEnvironment,
				renderType,
				extensionType,
				isConfiguring,
				isInserting,
				viewportSize,
			}),
		trackExtensionRemounted: ({
			mountCount,
		}: Omit<TrackExtensionRemountedPayload, 'source'>): void =>
			trackExtensionRemounted(createAnalyticsEvent, { source, mountCount }),
		trackBridgeSucceed: ({
			method,
			forgeEnvironment,
			additionalAttributes,
		}: Omit<TrackBridgeSucceedPayload, 'source'>): void =>
			trackBridgeSucceed(createAnalyticsEvent, {
				source,
				method,
				forgeEnvironment,
				additionalAttributes,
			}),
		trackBridgeFailed: ({
			method,
			errorName,
			forgeEnvironment,
			additionalAttributes,
		}: Omit<TrackBridgeFailedPayload, 'source'>): void =>
			trackBridgeFailed(createAnalyticsEvent, {
				source,
				errorName,
				method,
				forgeEnvironment,
				additionalAttributes,
			}),
		trackForgeFeatureFlagEvent: ({
			appType,
			baseUrl,
			appId,
			flagName,
			eventType,
			success,
			featureFlagEnv,
			forgeEnvironment,
		}: Omit<TrackForgeFeatureFlagEventPayload, 'source'>): void =>
			trackForgeFeatureFlagEvent(createAnalyticsEvent, {
				source,
				appType,
				baseUrl,
				appId,
				flagName,
				eventType,
				success,
				featureFlagEnv,
				forgeEnvironment,
			}),
		trackObjectStoreAction: ({
			appType,
			appId,
			action,
			forgeEnvironment,
		}: Omit<TrackObjectStoreActionPayload, 'source'>): void =>
			trackObjectStoreAction(createAnalyticsEvent, {
				source,
				appType,
				appId,
				action,
				forgeEnvironment,
			}),
		trackPermissionAction: ({
			appId,
			installationId,
			action,
		}: Omit<TrackPermissionActionPayload, 'source'>): void =>
			trackPermissionAction(createAnalyticsEvent, {
				source,
				appId,
				installationId,
				action,
			}),
	};
};

export type ForgeUiAnalyticsTracker = ReturnType<typeof createForgeUiAnalyticsEventTrackers>;

export const useForgeUiAnalyticsEvent = (): ForgeUiAnalyticsTracker => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { page: source } = useMetricsContext();

	const cachedTrackers = useMemoOne(
		() => createForgeUiAnalyticsEventTrackers({ createAnalyticsEvent, source }),
		[createAnalyticsEvent, source],
	);

	return cachedTrackers;
};
